<template>
  <div>
    <div class="inline-flex">
      <div class="bill-row">
        <app-tooltip
          v-if="!hideInfoBtn && el.Id > 0"
          :text="$t('info')"
          :style="{ height: '18px' }"
        >
          <template #activator>
            <app-icon
              class="bill-row__info q-mr-xs"
              name="info-circle-outline"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click.stop="openInfoDialog"
            />
          </template>
        </app-tooltip>
        <div
          :class="[
            'bill-row',
            { InvBillLink: el.ConnectionsWithValuesInvalidated },
            { 'draft-text': el.IsDraft && !isPayment || el.CreatedInCrm },
          ]"
          v-html="displayHtml"
        ></div>
      </div>
      <app-tooltip
        v-if="!hideRemoveBtn"
        :text="$t('remove')"
        :style="{ height: '16px' }"
      >
        <template #activator>
          <app-icon
            class="ml-1 bill-row__remove"
            name="close"
            width="16px"
            color="#979797"
            hover-color="#616161"
            cursor="pointer"
            @click.stop="detach"
          />
        </template>
      </app-tooltip>
    </div>
    <div
      v-if="el.Connections && el.Connections.filter(el => el.Project).length"
      :key="'imp-' + (el.Id ?? 0)"
    >
      <project-row
        v-for="(project, pi) in el.Connections.filter(el => el.Project)"
        :key="'imp-' + pi"
        class="q-mb-xs"
        :el="project.Project"
        :hide-remove-btn="true"
        @click.stop
      />
    </div>
  </div>
</template>

<script lang="ts">
import { useQuasar } from 'quasar';
import { Component, Prop, Vue } from 'vue-facing-decorator';
import { BillLinkedTypeDescriptor, PayStatus, BillType, BillSearchedTypeDescriptor } from '../../../../models/etc/types';
import { BillInfo, BillLinkedBase, BillSearchedBase, BillWithManyLinks, BillWithOneLink, PaymentInfo } from '../../../../models/interfaces/finances/finance-models';
import { BillRowProps } from '../../../../store/heartbeat-workers/useEntityStore';
import BillInfoDialog from './BillInfoDialog.vue';
import ProjectRow from './ProjectRow.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';

@Component({
  name: 'BillRow',
  components: { BillInfoDialog, ProjectRow, AppIcon, AppTooltip },
  emits: [
    'show-info',
    'detach',
    'info-closed'
  ]
})
export default class BillRow extends Vue {
  // === PROPS ===
  @Prop({ required: true }) readonly el!: BillLinkedBase | BillInfo | PaymentInfo | BillSearchedBase;
  @Prop({ type: Boolean, default: true }) readonly showType!: boolean;
  @Prop({ type: Number, default: BillRowProps.BillLinkedBase }) readonly billType!: number;
  @Prop({ type: Boolean, default: false }) readonly hideInfoBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly hideRemoveBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly showAmount!: boolean;
  @Prop({ type: Boolean, default: true }) readonly hideLinkAmounts!: boolean;
  @Prop({ type: Boolean, default: false }) readonly showProjects!: boolean;

  private $q = useQuasar();

  payStatusEnum = PayStatus;
  billTypeEnum = BillType;

  // showBillInfoMode = false

  get isPayment(): boolean {
    return (
        (this.billType === BillRowProps.BillLinkedBase || this.billType === BillRowProps.BillInfo || this.billType === BillRowProps.PaymentInfo) &&
        (this.el as BillLinkedBase).Descriptor === BillLinkedTypeDescriptor.Payment) ||
      (this.billType === BillRowProps.BillSearchedBase && (this.el as BillSearchedBase).Descriptor === BillSearchedTypeDescriptor.Payment);
  }

  openInfoDialog() {
    this.$q.dialog({
      component: BillInfoDialog,
      componentProps: {
        existingBillId: this.el.Id,
        isPayment: this.isPayment
      }
    })
    .onOk((payload?: { hadChanges?: boolean }) => {
      this.$emit('info-closed', payload ?? {})
    })
  }

  // === Computed: вся строка в v-html ===
  get displayHtml(): string {
    // if (this.el.isLoading) return '<span class="text-italic">Загрузка...</span>';

    const parts: string[] = [];

    // Тип документа
    if (!this.isPayment) {
      parts.push(`<span>${this.$t('inv_abbr')} </span>`);
    } else {
      parts.push(`<span>${this.$t('pl_abbr')} </span>`);
    }

    // 3. Входящий/Исходящий
    if (this.showType && this.el.Type === BillType.Incoming) {
      parts.push(`<span>${this.$t('inc_abbr')} </span>`);
    }
    if (this.showType && this.el.Type === BillType.Outgoing) {
      parts.push(`<span>${this.$t('out_abbr')} </span>`);
    }

    // 4. Номер
    if (this.el.Number) {
      parts.push(`<span class="green-text">${this.highlightText(this.el.Number)}</span>`);
    }

    // 5. Черновик / Создан в CRM
    // if ((this.el as BillLinkedBase).IsDraft || (this.el as PaymentInfo).CreatedInCrm) {
      // parts.push(`<span>[${this.$t('draft')}]</span>`);
    // }

    // 6. Экземпляр 1С
    parts.push(
      `<span title="${this.$t('one_c_instance')}"> ${this.el.Instance1C || this.$t('by_default')} </span>`
    );

    // 7. Дата
    parts.push(
      `<span class="q-mr-xs green-text"> ${this.$t('date_from_prefix')} ${this.formatDate(this.el.Date)},</span>`
    );

    // 8. Контрагент
    if (this.el.Counterparty) {
      parts.push(
        `<span class="q-mr-xs">${this.highlightText(this.el.Counterparty.Name)}</span>`
      );
    } else {
      // parts.push(`<span class="text-span">[без КА]</span>`);
    }

    // type-dependant values
    switch (this.billType) {
      case BillRowProps.BillLinkedBase:
        if (!this.hideLinkAmounts) {
          parts.push(`
            <span class="text-medium">${this.$t('total_lower')} </span>
            <span class="green-text">${(this.el as BillLinkedBase).TotalAmount}, </span>
          `);

          if (this.el.Descriptor === BillLinkedTypeDescriptor.BillWithManyLinks) {
            parts.push(`
              <span class="text-medium">${this.$t('here')} ${(this.el as BillWithManyLinks).LinkPercent}%: </span>
              <span class="green-text">${(this.el as BillWithManyLinks).LinkAmount} </span>
            `);
          } else if (this.el.Descriptor === BillLinkedTypeDescriptor.BillWithOneLink) {
            if ((this.el as BillWithOneLink).HasPayments) {
              parts.push(
                `<span class="q-mr-xs green-text">${(this.el as BillWithManyLinks).PayedPercent}% (${(this.el as BillWithManyLinks).PayedAmount})</span>`
              );
            } else {
              const statusText = this.getPayStatusText();
              parts.push(`<span class="q-mr-xs" style="white-space: pre;">${statusText}</span>`);
            }
          } else {
            const statusText = this.getPayStatusText();
            parts.push(`<span class="q-mr-xs" style="white-space: pre;">${statusText}</span>`);
          }
        }

        // %:
        // if (
        //   this.el.Descriptor === BillLinkedTypeDescriptor.BillWithManyLinks ||
        //   this.el.Descriptor === BillLinkedTypeDescriptor.BillWithOneLink
        // ) {
        //   parts.push(`
        //     <div class="q-mr-xs inline-block" style="margin-top: -2px; vertical-align: middle;">
        //       <q-progress-bar :percentage="${(this.el as BillWithManyLinks).PayedPercent}" size="sm" />
        //     </div>
        //   `);
        // }
        break;
      case BillRowProps.BillInfo:
        if (this.showAmount) {
          // Сумма
          parts.push(
            `<span class="green-text"> | ${(this.el as BillInfo).Amount}, </span>`
          );
          // Прикреплён к проекту
          if ((this.el as BillInfo).Project) {
            // parts.push(`<span> (прикреплён)</span>`);
            parts.push(`<span class="q-mr-xs green-text">${(this.el as BillInfo).Project.Name}</span>`)
          }
        }
        break;
      case BillRowProps.BillSearchedBase:
        if (this.showAmount) {
          // Сумма
          parts.push(
            `<span class="green-text"> | ${(this.el as BillSearchedBase).TotalAmount}, </span>`
          );
        }
        break;
      case BillRowProps.PaymentInfo:
        if (this.showAmount) {
          // Сумма
          parts.push(
            `<span class="green-text"> | ${(this.el as PaymentInfo).Amount}, </span>`
          );
        }
        break;
      default:
        break;
    }

    // 13. Заметка
    if (this.el.Note) {
      parts.push(`<span class="q-mr-xs">[${this.highlightText(this.el.Note)}]</span>`);
    }

    return parts.join('');
  }

  // === Методы ===
  highlightText(text: string): string {
    // Здесь должна быть логика фильтрации (например, из глобального поиска)
    // Пока — просто экранируем
    return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** Формат даты по UTC (дата с бэка в UTC, чтобы "от" показывало правильный день) */
  formatDate(date: Date | string): string {
    const d = new Date(date);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}.${month}.${year}`;
  }

  getPayStatusText(): string {
    switch ((this.el as BillWithOneLink).PayStatus) {
      case PayStatus.Cancelled: return this.$t('cancelled');
      case PayStatus.NotPayed: return this.$t('not_paid');
      case PayStatus.PartlyPayed: return this.$t('partially_paid');
      case PayStatus.Payed: return this.$t('paid');
      case PayStatus.Unknown: return this.$t('unknown');
      default: return '';
    }
  }

  // === События ===
  showInfo() {
    this.$emit('show-info', { Id: this.el.Id, isPayment: this.isPayment });
  }

  detach() {
    this.$emit('detach', { Id: this.el.Id, isPayment: this.isPayment });
  }
}
</script>

<style lang="scss" scoped>
.bill-row {
  display: inline;
}

.bill-row :deep(.green-text) {
  color: $primary !important;
  font-weight: 500;
}

.bill-row :deep(.text-medium) {
  font-weight: 500;
}

.bill-row :deep(.text-span) {
  color: $text-color;
  font-style: italic;
}

.InvBillLink {
  text-decoration: line-through;
  opacity: 0.6;
  display: inline;
}

.draft-text {
  color: #979797;
}

.inline-flex {
  display: inline-flex;
  align-items: flex-start;
}

.bill-row__info {
  margin-top: -2px;
}

.bill-row__remove {
  margin-top: 1px;
}
</style>
